import React, { useState } from "react";
import { useNavigate, Link, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/context/LanguageContext";
import { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Home, User, Building2, KeyRound } from "lucide-react";

const COUNTRY_CODES = ["+91", "+1", "+44", "+971", "+61", "+65", "+966"];

export default function Register() {
  const { register } = useAuth();
  const { t } = useLang();
  const navigate = useNavigate();
  const [sp] = useSearchParams();
  const redirect = sp.get("redirect");
  const [form, setForm] = useState({ name: "", email: "", password: "", confirm: "", phone: "", role: "customer", referral_code: "" });
  const [loading, setLoading] = useState(false);
  const [agreed, setAgreed] = useState(false);
  const [dial, setDial] = useState("+91");
  const upd = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!agreed) {
      toast.error("Please accept the Terms & Conditions and Privacy Policy to continue.");
      return;
    }
    if (form.role === "broker" && !form.phone.trim()) {
      toast.error("A working contact number is required to register as a mediator.");
      return;
    }
    if (form.password.length < 6) {
      toast.error("Password must be at least 6 characters.");
      return;
    }
    if (form.password !== form.confirm) {
      toast.error("Passwords do not match. Please re-enter the same password.");
      return;
    }
    setLoading(true);
    try {
      const u = await register({ name: form.name, email: form.email, password: form.password, phone: form.phone.trim() ? `${dial}${form.phone.replace(/[^0-9]/g, "")}` : "", role: form.role, referral_code: form.referral_code, terms_accepted: true });
      toast.success("Account created!");
      navigate(redirect || (u.role === "broker" || u.role === "owner" ? "/broker" : "/browse"));
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-[calc(100vh-4rem)] grid lg:grid-cols-2">
      {/* Brand panel */}
      <div className="hidden lg:flex flex-col justify-between bg-brand-ink text-white p-12 relative overflow-hidden">
        <div className="absolute inset-0 opacity-20" style={{ backgroundImage: "radial-gradient(circle at 25% 25%, #d9744f 0, transparent 42%), radial-gradient(circle at 75% 75%, #1f7a5c 0, transparent 45%)" }} />
        <div className="relative z-10 flex items-center gap-2">
          <div className="w-10 h-10 rounded-xl bg-brand-emerald flex items-center justify-center"><Home className="w-5 h-5 text-white" /></div>
          <span className="font-heading font-bold text-xl">My Property</span>
        </div>
        <div className="relative z-10">
          <h2 className="font-heading font-bold text-4xl leading-tight">Join thousands finding<br />homes they love.</h2>
          <p className="text-white/70 mt-4 max-w-sm">List as a mediator or owner, or browse as a customer — get started in seconds.</p>
        </div>
        <div className="relative z-10 text-white/50 text-sm">mypropertyas.com</div>
      </div>

      {/* Form panel */}
      <div className="flex items-center justify-center p-6 sm:p-10">
        <div className="w-full max-w-md">
          <h1 className="font-heading font-bold text-3xl text-brand-ink">{t("auth.registerTitle")}</h1>
          <p className="text-brand-muted text-sm mt-1 mb-7">{t("tagline")}</p>

          {/* Role selector — top */}
          <p className="text-xs font-semibold text-brand-muted uppercase tracking-wider mb-2">Register as</p>
          <div className="grid grid-cols-3 gap-3 mb-3">
            {[{ v: "customer", icon: User, l: t("auth.customer") }, { v: "broker", icon: Building2, l: t("auth.broker") }, { v: "owner", icon: KeyRound, l: t("auth.owner") }].map((r) => (
              <button
                key={r.v}
                type="button"
                onClick={() => upd("role", r.v)}
                data-testid={`role-${r.v}`}
                className={`flex flex-col items-center gap-2 p-3 rounded-xl border-2 transition-all ${form.role === r.v ? "border-brand-emerald bg-brand-emerald/5" : "border-brand-line hover:border-brand-emerald/40"}`}
              >
                <r.icon className={`w-5 h-5 ${form.role === r.v ? "text-brand-emerald" : "text-brand-muted"}`} />
                <span className={`text-xs font-semibold ${form.role === r.v ? "text-brand-emerald" : "text-brand-muted"}`}>{r.l}</span>
              </button>
            ))}
          </div>
          {form.role === "owner" && <p className="text-xs text-brand-muted mb-4">Individual owners pay a one-time listing fee per property. Your listing goes live after admin confirms payment & approval.</p>}

          <form onSubmit={submit} className="space-y-4">
            <div>
              <Label>{t("auth.name")}</Label>
              <Input value={form.name} onChange={(e) => upd("name", e.target.value)} required data-testid="reg-name" className="mt-1.5" />
            </div>
            <div>
              <Label>{t("auth.email")}</Label>
              <Input type="email" value={form.email} onChange={(e) => upd("email", e.target.value)} required data-testid="reg-email" className="mt-1.5" />
            </div>
            <div>
              <Label>{t("auth.phone")}{form.role === "broker" && <span className="text-brand-terracotta"> *</span>}</Label>
              <div className="flex gap-2 mt-1.5">
                <select value={dial} onChange={(e) => setDial(e.target.value)} data-testid="reg-country-code" className="w-24 shrink-0 rounded-xl border border-brand-line bg-background px-2 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-emerald/40">
                  {COUNTRY_CODES.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
                <Input value={form.phone} onChange={(e) => upd("phone", e.target.value)} required={form.role === "broker"} placeholder={form.role === "broker" ? "Working contact number" : "Mobile number"} data-testid="reg-phone" className="flex-1" />
              </div>
            </div>
            <div>
              <Label>{t("auth.password")}</Label>
              <Input type="password" value={form.password} onChange={(e) => upd("password", e.target.value)} required data-testid="reg-password" className="mt-1.5" />
            </div>
            <div>
              <Label>Confirm Password</Label>
              <Input type="password" value={form.confirm} onChange={(e) => upd("confirm", e.target.value)} required data-testid="reg-confirm-password" placeholder="Re-enter your password" className="mt-1.5" />
              {form.confirm && form.password !== form.confirm && <p className="text-xs text-brand-terracotta mt-1" data-testid="reg-password-mismatch">Passwords do not match</p>}
            </div>
            {form.role === "broker" && (
              <div>
                <Label>Referral Code <span className="text-brand-muted text-xs">(optional)</span></Label>
                <Input value={form.referral_code} onChange={(e) => upd("referral_code", e.target.value)} placeholder="Referred by another mediator? Enter their code" data-testid="reg-referral" className="mt-1.5" />
              </div>
            )}
            <label className="flex items-start gap-2.5 text-sm text-brand-muted cursor-pointer">
              <input type="checkbox" checked={agreed} onChange={(e) => setAgreed(e.target.checked)} data-testid="reg-terms-checkbox" className="mt-0.5 w-4 h-4 accent-brand-emerald shrink-0" />
              <span>I agree to the <Link to="/terms" target="_blank" className="text-brand-emerald hover:underline">Terms &amp; Conditions</Link> and <Link to="/privacy" target="_blank" className="text-brand-emerald hover:underline">Privacy Policy</Link>.</span>
            </label>
            <Button type="submit" disabled={loading || !agreed} data-testid="reg-submit" className="w-full rounded-full bg-brand-emerald hover:bg-brand-emeraldHover h-11">
              {loading ? t("loading") : t("auth.registerBtn")}
            </Button>
          </form>

          <div className="mt-7 pt-5 border-t border-brand-line text-center">
            <p className="text-sm text-brand-muted">{t("auth.haveAccount")}</p>
            <Link to={redirect ? `/login?redirect=${encodeURIComponent(redirect)}` : "/login"} data-testid="go-login" className="inline-block mt-2 text-brand-emerald font-semibold hover:underline">{t("nav.login")} →</Link>
          </div>
        </div>
      </div>
    </div>
  );
}
